(function () {
  const NYLO = (window.NYLO = window.NYLO || {});

  // Money formatter — integer cents in, shop money format out. No float math
  // beyond display division.
  function formatMoney(cents, format) {
    const fmt = format || NYLO.moneyFormat || '${{amount}}';
    const value = Math.round(Number(cents) || 0);
    const negative = value < 0;
    const abs = Math.abs(value);
    const comma = (num) => String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const dot = (num) => String(num).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const pad2 = (num) => String(num).padStart(2, '0');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
      let out;
      switch (key) {
        case 'amount':
          out = comma(Math.floor(abs / 100)) + '.' + pad2(abs % 100);
          break;
        case 'amount_no_decimals':
          out = comma(Math.round(abs / 100));
          break;
        case 'amount_with_comma_separator':
          out = dot(Math.floor(abs / 100)) + ',' + pad2(abs % 100);
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = dot(Math.round(abs / 100));
          break;
        default:
          out = comma(Math.floor(abs / 100)) + '.' + pad2(abs % 100);
      }
      return (negative ? '-' : '') + out;
    });
  }
  NYLO.formatMoney = formatMoney;

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const header = document.getElementById('header');
  if (header) {
    const solid = header.classList.contains('solid');
    const onScroll = () => {
      if (solid) return;
      header.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const menuToggle = document.getElementById('menuToggle');
  const navOverlay = document.getElementById('navOverlay');
  const mobileNav = document.getElementById('mobileNav');

  // Mobile nav overlay. Full-screen dialog opened by #menuToggle.
  // Close on: close button, Escape, backdrop click, link click, or the
  // overlay's own search button (which hands off to the search overlay).
  if (menuToggle && navOverlay) {
    const navInner = navOverlay.querySelector('.nav-overlay__inner');
    const navClose = navOverlay.querySelector('#navOverlayClose, .nav-overlay__close');
    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let navHideTimer = null;

    const navFocusables = () =>
      Array.from(navOverlay.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    const navIsOpen = () => navOverlay.classList.contains('is-open');

    function openNav() {
      if (navIsOpen()) return;
      clearTimeout(navHideTimer);
      navOverlay.hidden = false;
      void navOverlay.offsetHeight; // reflow so the open transition runs
      navOverlay.classList.add('is-open');
      document.body.classList.add('nav-open');
      menuToggle.setAttribute('aria-expanded', 'true');
      // Deferred: the panel may still be visibility:hidden mid-transition,
      // and focus() is a no-op on a hidden element.
      window.setTimeout(() => {
        if (!navIsOpen()) return;
        const first = navOverlay.querySelector('.nav-overlay__links a') || navFocusables()[0];
        first?.focus();
      }, 50);
    }

    function closeNav(restoreFocus) {
      if (!navIsOpen()) return;
      navOverlay.classList.remove('is-open');
      document.body.classList.remove('nav-open');
      menuToggle.setAttribute('aria-expanded', 'false');
      clearTimeout(navHideTimer);
      navHideTimer = window.setTimeout(() => {
        if (!navIsOpen()) navOverlay.hidden = true;
      }, 320);
      if (restoreFocus !== false) menuToggle.focus();
    }

    menuToggle.setAttribute('aria-controls', 'navOverlay');
    if (!menuToggle.hasAttribute('aria-expanded')) menuToggle.setAttribute('aria-expanded', 'false');

    menuToggle.addEventListener('click', () => {
      if (navIsOpen()) closeNav();
      else openNav();
    });
    navClose?.addEventListener('click', () => closeNav());

    // Backdrop: any click that is not inside the panel closes.
    navOverlay.addEventListener('click', (e) => {
      if (navInner && navInner.contains(e.target)) return;
      closeNav();
    });

    // Links navigate; close so the overlay is not left open on back-nav.
    navOverlay.querySelectorAll('.nav-overlay__links a, .nav-overlay__meta a').forEach((a) => {
      a.addEventListener('click', () => closeNav(false));
    });

    // Search hand-off. Registered before the search module binds
    // [data-open-search], so the overlay closes first and search takes focus.
    navOverlay.querySelectorAll('[data-open-search]').forEach((btn) => {
      btn.addEventListener('click', () => closeNav(false));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && navIsOpen()) {
        e.preventDefault();
        closeNav();
      }
    });

    // Focus trap while open.
    navOverlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !navIsOpen()) return;
      const items = navFocusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !navOverlay.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    });
  } else if (menuToggle && mobileNav && header) {
    // Legacy dropdown nav (kept for any template still rendering #mobileNav).
    menuToggle.addEventListener('click', () => {
      const open = mobileNav.classList.toggle('open');
      if (!header.classList.contains('solid')) {
        header.classList.toggle('scrolled', open || window.scrollY > 40);
      }
    });
    mobileNav.querySelectorAll('a').forEach((a) =>
      a.addEventListener('click', () => mobileNav.classList.remove('open'))
    );
  }

  // Catalog dropdown (desktop header), WAI disclosure navigation pattern.
  // Catalog itself stays a plain link to the full catalog. The small button
  // after it ([data-nav-toggle], shown only on keyboard focus) owns
  // aria-expanded, so screen reader users in browse mode can open the panel.
  // Opens: mouse hover after a 120 ms intent delay (so a pass from the header
  // down to the filter sidebar does not set it off), keyboard focus on
  // Catalog at once, or the button. Closes: Escape from anywhere on the page
  // (WCAG 1.4.13, the panel covers the top of the filters), an outside click,
  // focus leaving, or the mouse leaving after a 200 ms grace delay so a
  // diagonal move to the panel does not drop it. Touch taps skip the panel
  // and follow the Catalog link. Without JS the CSS :hover / :focus-within
  // fallback applies instead (.js-dropdown switches it off).
  document.querySelectorAll('[data-nav-dropdown]').forEach((item) => {
    const trigger = item.querySelector('[data-nav-trigger]');
    const toggle = item.querySelector('[data-nav-toggle]');
    const panel = item.querySelector('.nav-dropdown');
    if (!trigger || !panel) return;
    const links = () => Array.from(panel.querySelectorAll('a[href]'));
    const isOpen = () => item.classList.contains('is-open');
    let openTimer = null;
    let closeTimer = null;
    let quietFocus = false;
    item.classList.add('js-dropdown');

    function open() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (isOpen()) return;
      item.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (!isOpen()) return;
      item.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }
    // Move focus without the focusin handler reopening the panel.
    function focusQuietly(el) {
      quietFocus = true;
      el.focus();
      quietFocus = false;
    }
    const keyboardFocus = (el) => {
      try {
        return el.matches(':focus-visible');
      } catch (err) {
        return true;
      }
    };

    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch') return;
      clearTimeout(closeTimer);
      if (isOpen()) return;
      clearTimeout(openTimer);
      openTimer = window.setTimeout(open, 120);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch') return;
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (isOpen()) closeTimer = window.setTimeout(close, 200);
    });
    item.addEventListener('focusin', (e) => {
      // The button opens and closes on activation, not on focus.
      if (quietFocus || e.target === toggle) return;
      if (e.target !== trigger || keyboardFocus(trigger)) open();
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) close();
    });
    if (toggle) {
      toggle.addEventListener('click', () => {
        if (isOpen()) close();
        else open();
      });
    }
    // Arrow keys move through the items while focus is in the dropdown.
    item.addEventListener('keydown', (e) => {
      const items = links();
      const idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && items.length) {
        e.preventDefault();
        open();
        items[idx < 0 ? 0 : Math.min(idx + 1, items.length - 1)].focus();
      } else if (e.key === 'ArrowUp' && idx >= 0) {
        e.preventDefault();
        if (idx === 0) trigger.focus();
        else items[idx - 1].focus();
      }
    });
    // Escape closes a panel opened by hover too, wherever focus is. Focus
    // only moves when it was inside the panel (the links are about to hide).
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !isOpen()) return;
      const fromPanel = panel.contains(document.activeElement);
      close();
      if (fromPanel) {
        e.preventDefault();
        focusQuietly(toggle || trigger);
      }
    });
    panel.addEventListener('click', (e) => {
      if (e.target.closest('a')) close();
    });
    document.addEventListener('click', (e) => {
      if (isOpen() && !item.contains(e.target)) close();
    });
  });

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el, i) => {
    el.style.transitionDelay = (i % 4) * 50 + 'ms';
    io.observe(el);
  });

  // Hero crossfade + controls (homepage only)
  const heroImgs = [...document.querySelectorAll('.hero-bg img')];
  const kickerEl = document.getElementById('heroKicker');
  const titleEl = document.getElementById('heroTitle');
  const heroPrev = document.querySelector('[data-hero-prev]');
  const heroNext = document.querySelector('[data-hero-next]');
  if (heroImgs.length && kickerEl && titleEl) {
    let hi = 0;
    let heroTimer = null;
    const HERO_INTERVAL = 5500;

    const goToHero = (index, userInitiated) => {
      heroImgs[hi].classList.remove('active');
      hi = (index + heroImgs.length) % heroImgs.length;
      heroImgs[hi].classList.add('active');
      // Only swap the heading when the slide carries its own text. Slides
      // without kicker/title rotate the background under a fixed heading
      // (issue #8 follow-up: background rotates, heading stays).
      if (heroImgs[hi].dataset.kicker) kickerEl.textContent = heroImgs[hi].dataset.kicker;
      if (heroImgs[hi].dataset.title) titleEl.textContent = heroImgs[hi].dataset.title;
      if (userInitiated) startHeroTimer();
    };

    const startHeroTimer = () => {
      clearInterval(heroTimer);
      heroTimer = setInterval(() => goToHero(hi + 1), HERO_INTERVAL);
    };

    heroPrev?.addEventListener('click', () => goToHero(hi - 1, true));
    heroNext?.addEventListener('click', () => goToHero(hi + 1, true));
    // A single slide has nothing to rotate to: leaving the timer running would
    // re-trigger the crossfade on the same image every interval.
    if (heroImgs.length > 1) startHeroTimer();
  }


  // ---- PDP review pagination ----
  // 6 reviews per page (fills the two-column grid). The server renders Judge.me's
  // widget metafield, which carries only the 5 newest, so on load page 1 is filled to 6
  // and every page is read live from Judge.me's public widget data (the JSON its own
  // widget reads): a review hidden in Judge.me never shows, and names and badges match
  // Judge.me. If Judge.me cannot be reached, pages come from the theme asset
  // reviews--{id}.json, which the toolkit refreshes every Saturday (com.nylo.reviewssync);
  // if that fails too, the server's 5 stay and the pager becomes a note.
  const revGrid = document.querySelector('.pdp-review-grid[data-reviews-src]');
  const revPager = document.querySelector('[data-review-pager]');
  if (revGrid) {
    const per = parseInt(revGrid.dataset.per, 10) || 6;
    const total = parseInt(revGrid.dataset.total, 10) || 0;
    let pages = Math.max(1, Math.ceil(total / per));
    const firstPage = revGrid.innerHTML;
    const jmShop = revGrid.dataset.jmShop || '';
    const jmProduct = revGrid.dataset.jmProduct || '';
    const prevBtn = revPager ? revPager.querySelector('[data-pager-prev]') : null;
    const nextBtn = revPager ? revPager.querySelector('[data-pager-next]') : null;
    const status = revPager ? revPager.querySelector('[data-pager-status]') : null;
    let all = null;
    let page = 1;
    let queue = Promise.resolve();
    const seenPages = {};

    const starRow = (rating) => {
      let s = '';
      for (let i = 1; i <= 5; i++) s += i <= rating ? '\u2605' : '\u2606';
      return s;
    };

    const setPage = (n, scroll) => {
      page = n;
      if (prevBtn) prevBtn.disabled = page <= 1;
      if (nextBtn) nextBtn.disabled = page >= pages;
      if (status) status.textContent = 'Page ' + page + ' of ' + pages;
      if (scroll) revGrid.closest('section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const renderList = (list) => {
      revGrid.innerHTML = '';
      list.forEach((r) => {
        const card = document.createElement('blockquote');
        card.className = 'pdp-review-card';
        const stars = document.createElement('div');
        stars.className = 'pdp-review-stars';
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', 'Rated ' + r.rating + ' out of 5 stars');
        stars.textContent = starRow(r.rating);
        card.appendChild(stars);
        if (r.title) {
          const t = document.createElement('strong');
          t.className = 'pdp-review-title';
          t.textContent = r.title;
          card.appendChild(t);
        }
        if (r.body) {
          const p = document.createElement('p');
          p.textContent = '\u201C' + r.body + '\u201D';
          card.appendChild(p);
        }
        const foot = document.createElement('footer');
        const cite = document.createElement('cite');
        cite.textContent = r.reviewer_name || 'Anonymous';
        foot.appendChild(cite);
        if (r.verified_buyer) {
          const v = document.createElement('span');
          v.className = 'pdp-review-verified';
          v.textContent = 'Verified buyer';
          foot.appendChild(v);
        }
        if (r.created_at) {
          const tm = document.createElement('time');
          tm.dateTime = r.created_at;
          tm.textContent = new Date(r.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          foot.appendChild(tm);
        }
        card.appendChild(foot);
        revGrid.appendChild(card);
      });
    };

    // Judge.me's body_html as plain text. DOMParser builds an inert document (no
    // scripts run, no images load) and only text reaches the page.
    const textOf = (html) => {
      const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    };
    const fromJudgeme = (w) => ({
      rating: w.rating,
      title: String(w.title || '').trim(),
      body: textOf(w.body_html),
      reviewer_name: w.is_anonymous_reviewer === false ? (String(w.reviewer_name || '').trim() || 'Anonymous') : 'Anonymous',
      verified_buyer: w.verified_buyer === true,
      created_at: w.created_at,
    });

    const livePage = async (n) => {
      if (!jmShop || !jmProduct || typeof fetch !== 'function') return null;
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 6000) : null;
      try {
        const q = new URLSearchParams({
          url: jmShop, shop_domain: jmShop, platform: 'shopify',
          page: String(n), per_page: String(per), product_id: jmProduct,
        });
        const res = await fetch('https://judge.me/reviews/reviews_for_widget?' + q.toString(),
          { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined });
        if (!res.ok) return null;
        const data = await res.json();
        if (!data || !Array.isArray(data.reviews)) return null;
        const list = data.reviews
          .filter((w) => w && Number.isInteger(w.rating) && w.rating >= 1 && w.rating <= 5)
          .map(fromJudgeme);
        const tp = data.pagination && parseInt(data.pagination.total_pages, 10);
        if (tp > 0) pages = tp;
        return list;
      } catch (e) {
        return null;
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    const ensureFile = async () => {
      if (all) return true;
      try {
        const res = await fetch(revGrid.dataset.reviewsSrc, { credentials: 'omit' });
        if (!res.ok) throw new Error('asset missing');
        const data = await res.json();
        all = (data.reviews || []).filter((r) => r && r.rating);
        return all.length > 0;
      } catch (e) {
        return false;
      }
    };

    const giveUp = () => {
      revGrid.innerHTML = firstPage;
      if (revPager) {
        const note = document.createElement('p');
        note.className = 'pdp-reviews-more';
        note.textContent = 'Showing the most recent reviews.';
        revPager.replaceWith(note);
      }
    };

    const show = async (n, scroll) => {
      if (n < 1 || n > pages) return;
      let list = seenPages[n] || await livePage(n);
      if (!list || !list.length) {
        list = (await ensureFile()) ? all.slice((n - 1) * per, n * per) : null;
      }
      if (list && list.length) {
        seenPages[n] = list;
        renderList(list);
        setPage(n, scroll);
      } else {
        giveUp();
      }
    };

    // Clicks wait for any page still loading, so a fast click is never lost.
    const go = (delta) => {
      queue = queue.then(() => show(page + delta, true)).catch(() => {});
    };

    prevBtn?.addEventListener('click', () => go(-1));
    nextBtn?.addEventListener('click', () => go(1));

    // Fill page 1 to a full page without scrolling (the server shows the 5 newest).
    if (total > revGrid.children.length) queue = queue.then(() => show(1, false)).catch(() => {});
  }

  // Product tabs (homepage trending row)
  const tabs = [...document.querySelectorAll('.tab')];
  const tabProducts = [...document.querySelectorAll('#productRow .product')];
  if (tabs.length && tabProducts.length) {
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');
        const f = tab.dataset.filter;
        tabProducts.forEach((p) => {
          p.style.display = f === 'all' || p.dataset.cat === f ? '' : 'none';
        });
      });
    });
  }

  // Toast — the single piece of feedback after an add. Visible state class is
  // `.toast.is-visible` (shared DOM contract). Uses the markup-rendered .toast
  // when present, otherwise creates one so every template has feedback.
  let toastEl = document.querySelector('.toast');
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }
  let toastTimer = null;
  function showToast(msg) {
    if (!toastEl) return;
    toastEl.textContent = msg || 'Added to bag';
    clearTimeout(toastTimer);
    // `is-visible` is the contract class. `show` is a transitional alias for
    // the pre-redesign stylesheet; drop it once the CSS rename has landed.
    toastEl.classList.add('is-visible', 'show');
    toastTimer = window.setTimeout(() => toastEl.classList.remove('is-visible', 'show'), 2500);
  }
  NYLO.showToast = showToast;

  // Header bag counts. Preserves whatever wrapper the markup uses: "(2)" stays
  // parenthesised, a bare number stays bare.
  function setCartCounts(count) {
    const n = Math.max(0, parseInt(count, 10) || 0);
    NYLO.cartCount = n;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = /^\s*\(.*\)\s*$/.test(el.textContent) ? '(' + n + ')' : String(n);
    });
  }

  // Free-shipping progress bar. Threshold in cents lives on #freeShipBar.
  // Takes the /cart.js object. Gift card lines never count toward the threshold
  // (gift-set plan, 2026-10-30; templates/cart.liquid applies the same rule):
  // the total is the bag total minus every gift card line, and a bag holding
  // only gift cards hides the bar, since nothing in it ships.
  function freeShipTotals(cart) {
    const items = (cart && cart.items) || [];
    let giftCents = 0;
    let shippable = 0;
    items.forEach((item) => {
      if (item && item.gift_card) giftCents += parseInt(item.final_line_price, 10) || 0;
      else shippable += 1;
    });
    const cartTotal = parseInt(cart && cart.total_price, 10) || 0;
    return { total: Math.max(0, cartTotal - giftCents), giftOnly: items.length > 0 && shippable === 0 };
  }
  function updateFreeShipBar(cart) {
    const bar = document.getElementById('freeShipBar');
    if (!bar) return;
    const threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    if (threshold <= 0) return;
    const totals = freeShipTotals(cart);
    bar.hidden = totals.giftOnly;
    const total = totals.total;
    const pct = Math.max(0, Math.min(100, Math.floor((total * 100) / threshold)));
    const unlocked = total >= threshold;

    const fill = bar.querySelector('[data-freeship-fill]') || bar.querySelector('.free-ship-fill');
    if (fill) {
      fill.style.width = pct + '%';
      const track = fill.closest('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(pct));
    }
    const text = bar.querySelector('[data-freeship-text]') || bar.querySelector('.free-ship-msg');
    if (text) {
      text.textContent = unlocked
        ? 'Free shipping unlocked'
        : 'You are ' + formatMoney(threshold - total) + ' away from free shipping';
      text.classList.toggle('free-ship-unlocked', unlocked);
    }
    bar.classList.toggle('is-unlocked', unlocked);
  }

  // The words a failed cart call shows in the toast: Shopify's description when it
  // is text, else its message, else the fallback. A 422 can carry field errors as
  // an object in description (Dawn's product-form.js passes errors || description
  // to its recipient form), and an object must never print as "[object Object]".
  function cartErrorText(err, fallback) {
    if (err && typeof err.description === 'string' && err.description) return err.description;
    if (err && typeof err.message === 'string' && err.message) return err.message;
    return fallback;
  }

  // Shared add-to-cart. Adds, refreshes header counts + free-ship bar, toasts.
  // Resolves with the cart on success (true when the item was added but the
  // bag refresh failed) and undefined on failure. onError, when given, also
  // receives a failed add (the gift card form prints it inline).
  function addToCart(body, isFormData, onError) {
    const opts = isFormData
      ? { method: 'POST', body: body }
      : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    let added = false;
    return fetch('/cart/add.js', opts)
      .then((res) => {
        if (!res.ok) return res.json().then((err) => Promise.reject(err));
        return res.json();
      })
      .then(() => {
        added = true;
        return fetch('/cart.js').then((res) => res.json());
      })
      .then((cart) => {
        setCartCounts(cart.item_count);
        updateFreeShipBar(cart);
        showToast('Added to bag');
        return cart;
      })
      .catch((err) => {
        if (added) {
          // The item is in the bag; only the refresh of the count failed. Never
          // report that as a failed add: the gift card form would keep its
          // recipient and invite a second, final-sale card. The count catches up
          // on the next page.
          showToast('Added to bag');
          return true;
        }
        if (typeof onError === 'function') onError(err);
        showToast(cartErrorText(err, 'Could not add to bag'));
      });
  }

  // PDP gallery
  const mainImg = document.getElementById('pdpMainImg');
  const thumbs = document.querySelectorAll('[data-pdp-thumb]');
  thumbs.forEach((btn) => {
    btn.addEventListener('click', () => {
      thumbs.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      if (mainImg) {
        // srcset wins over src in candidate selection: clear it or the swap is a no-op.
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = btn.dataset.pdpThumb;
      }
    });
  });

  // PDP product data (embedded JSON blob from product.liquid)
  const pdpDataEl = document.getElementById('pdpData');
  let pdpData = null;
  if (pdpDataEl) {
    try {
      pdpData = JSON.parse(pdpDataEl.textContent);
    } catch (e) {
      pdpData = null;
    }
  }
  const variants = (pdpData && pdpData.variants) || [];
  const pdpForm = document.getElementById('pdpForm');
  const idInput = pdpForm ? pdpForm.querySelector('input[name="id"]') : null;
  const planInput = pdpForm ? pdpForm.querySelector('input[name="selling_plan"]') : null;
  const pdpPriceEl = document.querySelector('[data-pdp-price]');
  const pdpCta = document.querySelector('[data-pdp-cta]');
  const qtyInput = document.getElementById('pdpQty');

  function currentVariant() {
    if (!variants.length) return null;
    if (idInput && idInput.value) {
      const found = variants.find((v) => String(v.id) === String(idInput.value));
      if (found) return found;
    }
    return variants[0];
  }

  function selectedOptionValues() {
    const sel = [];
    document.querySelectorAll('.option-group').forEach((group, i) => {
      const pill = group.querySelector('.option-pill.active');
      if (!pill) return;
      let idx = parseInt(group.dataset.optionIndex ?? pill.dataset.optionIndex ?? i, 10);
      if (Number.isNaN(idx)) idx = i;
      sel[idx] = pill.dataset.value;
    });
    return sel;
  }

  function resolveVariant() {
    if (!variants.length) return null;
    const sel = selectedOptionValues();
    if (!sel.length) return currentVariant();
    return (
      variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return sel.every((val, i) => val == null || opts[i] === val);
      }) || null
    );
  }

  function applyVariant(variant) {
    if (!variant) {
      if (pdpCta) {
        pdpCta.disabled = true;
        pdpCta.textContent = 'Unavailable';
      }
      return;
    }
    if (idInput) idInput.value = String(variant.id);
    if (pdpPriceEl) pdpPriceEl.textContent = formatMoney(variant.price);
    const media = variant.featured_media || variant.featured_image;
    if (media && media.id != null) {
      const thumb = document.querySelector('[data-pdp-thumb][data-media-id="' + media.id + '"]');
      if (thumb) {
        thumb.click();
      } else if (mainImg) {
        const src = (media.preview_image && media.preview_image.src) || media.src;
        if (src) {
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = src;
        }
      }
    }
    updatePdpCta();
  }

  // Option pills
  document.querySelectorAll('.option-group').forEach((group) => {
    group.querySelectorAll('.option-pill').forEach((pill) => {
      pill.addEventListener('click', () => {
        group.querySelectorAll('.option-pill').forEach((p) => p.classList.remove('active'));
        pill.classList.add('active');
        if (variants.length) applyVariant(resolveVariant());
      });
    });
  });

  // PDP purchase type + quantity
  function selectedPurchase() {
    return document.querySelector('.purchase-option input[type="radio"]:checked');
  }
  function syncPurchaseActive() {
    document.querySelectorAll('.purchase-option').forEach((opt) => {
      const on = !!opt.querySelector('input[type="radio"]:checked');
      opt.classList.toggle('is-active', on);
    });
  }
  // Selling plan id for the current selection: '' for one-time, else the
  // frequency select's value (a selling plan id) or the radio's own value.
  function selectedPlanId() {
    const purchase = selectedPurchase();
    if (!purchase) return '';
    const value = purchase.value || '';
    if (!value || value === 'once') return '';
    const opt = purchase.closest('.purchase-option');
    const freq = opt ? opt.querySelector('[data-subscribe-freq]') : null;
    if (freq && freq.value) return freq.value;
    return value === 'subscribe' ? '' : value;
  }
  function unitCents(variant) {
    const planId = selectedPlanId();
    if (variant) {
      if (planId) {
        const allocs = variant.selling_plan_allocations || [];
        const alloc = allocs.find(
          (a) =>
            String(a.selling_plan_id) === String(planId) ||
            (a.selling_plan && String(a.selling_plan.id) === String(planId))
        );
        if (alloc && alloc.price != null) return alloc.price;
      }
      return variant.price;
    }
    // Static fallback (no embedded product JSON): dataset decimal dollars.
    const purchase = selectedPurchase();
    const dec = parseFloat(
      purchase?.dataset.purchasePrice || pdpCta?.dataset.purchasePrice || '25'
    );
    return Math.round(dec * 100);
  }
  function updatePdpCta() {
    if (!pdpCta) return;
    const qty = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);
    if (qtyInput) qtyInput.value = String(qty);
    const planId = selectedPlanId();
    if (planInput) planInput.value = planId;
    const variant = currentVariant();
    if (variant && variant.available === false) {
      pdpCta.disabled = true;
      pdpCta.textContent = 'Sold out';
      return;
    }
    pdpCta.disabled = false;
    pdpCta.textContent = 'Add to bag · ' + formatMoney(unitCents(variant) * qty);
  }
  document.querySelectorAll('.purchase-option').forEach((opt) => {
    const input = opt.querySelector('input[type="radio"]');
    input?.addEventListener('change', () => {
      syncPurchaseActive();
      updatePdpCta();
    });
    opt.addEventListener('click', (e) => {
      if (e.target.closest('select, a, button')) return;
      if (!input || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
  document.querySelectorAll('[data-subscribe-freq]').forEach((select) => {
    select.addEventListener('mousedown', (e) => e.stopPropagation());
    select.addEventListener('click', (e) => e.stopPropagation());
    select.addEventListener('focus', () => {
      const opt = select.closest('.purchase-option');
      const input = opt?.querySelector('input[type="radio"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    select.addEventListener('change', () => {
      const opt = select.closest('.purchase-option');
      const input = opt?.querySelector('input[type="radio"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      updatePdpCta();
    });
  });
  syncPurchaseActive();
  document.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
    if (!qtyInput) return;
    qtyInput.value = String(Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1));
    updatePdpCta();
  });
  document.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
    if (!qtyInput) return;
    qtyInput.value = String(Math.min(99, (parseInt(qtyInput.value, 10) || 1) + 1));
    updatePdpCta();
  });
  qtyInput?.addEventListener('change', updatePdpCta);
  updatePdpCta();

  // Gift card recipient form (templates/product.liquid, gift cards only; gift-set
  // plan, 2026-10-30). Shopify reads line item properties: properties[Recipient
  // email], [Recipient name], [Message], [Send on] (yyyy-mm-dd, up to 90 days
  // ahead), the control flag properties[__shopify_send_gift_card_to_recipient]
  // and properties[__shopify_offset] (minutes, getTimezoneOffset()). This follows
  // Dawn's recipient-form.js: enable the checkbox and disable the no-JS
  // "if_present" control; while unticked, every recipient input and the offset
  // are disabled, so nothing is sent; ticked, the email is required (the
  // browser's own validation runs before submit). cart/add.js answers 422 with
  // { status, message, description } (Ajax API reference); Dawn's
  // recipient-form.js also reads an errors object keyed by field (email, name,
  // message, send_on) holding arrays of messages, from errors or, failing that,
  // from description. Every shape prints inline, and an object never prints as text.
  // After a successful add the form resets, so a second card never reuses the
  // first recipient by accident.
  const giftRecipient = (function giftCardRecipient() {
    const root = document.querySelector('[data-gc-recipient]');
    if (!root) return null;
    const toggle = root.querySelector('[data-gc-toggle]');
    if (!toggle) return null;
    const control = root.querySelector('[data-gc-control]');
    const offset = root.querySelector('[data-gc-offset]');
    const summary = root.querySelector('[data-gc-error]');
    const counter = root.querySelector('[data-gc-count]');
    const inputs = Array.from(root.querySelectorAll('[data-gc-input]'));
    const byKey = {};
    inputs.forEach((el) => {
      byKey[el.getAttribute('data-gc-input')] = el;
    });
    const LABELS = { email: 'Recipient email', name: 'Recipient name', message: 'Message', send_on: 'Send on date' };

    // The shopper's own today and today + 90 days, in local time.
    const pad2 = (n) => String(n).padStart(2, '0');
    const isoDate = (d) => d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    if (byKey.send_on) {
      const now = new Date();
      byKey.send_on.min = isoDate(now);
      byKey.send_on.max = isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90));
    }
    if (offset) offset.value = String(new Date().getTimezoneOffset());

    function updateCount() {
      if (!counter || !byKey.message) return;
      const max = parseInt(byKey.message.getAttribute('maxlength'), 10) || 200;
      const used = byKey.message.value.length;
      counter.textContent = used ? used + ' of ' + max + ' characters' : 'Up to ' + max + ' characters.';
    }

    function fieldError(key) {
      return root.querySelector('[data-gc-field-error="' + key + '"]');
    }

    function clearErrors() {
      if (summary) {
        summary.hidden = true;
        summary.textContent = '';
      }
      inputs.forEach((el) => {
        el.removeAttribute('aria-invalid');
        const msg = fieldError(el.getAttribute('data-gc-input'));
        if (msg) {
          msg.hidden = true;
          msg.textContent = '';
        }
      });
    }

    function sync() {
      const on = toggle.checked;
      root.classList.toggle('is-open', on);
      inputs.forEach((el) => {
        el.disabled = !on;
      });
      if (offset) offset.disabled = !on;
      if (byKey.email) byKey.email.required = on;
      if (!on) clearErrors();
    }

    // "Recipient email", "properties[Send on]" and "send_on" all map to a field.
    function fieldKey(raw) {
      const k = String(raw)
        .toLowerCase()
        .replace(/^properties\[(.*)\]$/, '$1')
        .replace(/^recipient[\s_-]*/, '')
        .trim()
        .replace(/[\s-]+/g, '_');
      return Object.prototype.hasOwnProperty.call(LABELS, k) ? k : null;
    }

    function showErrors(err) {
      clearErrors();
      if (!err) return;
      const general = [];
      let firstField = null;
      // Field errors come as an errors object, or (as Dawn allows) as an object
      // in description; a string description is a sentence for the summary.
      const errors =
        err.errors && typeof err.errors === 'object'
          ? err.errors
          : err.description && typeof err.description === 'object'
            ? err.description
            : err.errors;
      if (errors && typeof errors === 'object') {
        Object.keys(errors).forEach((raw) => {
          const list = [].concat(errors[raw]).filter((m) => m != null && m !== '').map(String);
          if (!list.length) return;
          const key = fieldKey(raw);
          let text = list.join(', ');
          // Shopify's field messages can start in lower case ("is invalid"). A form
          // field gets its label in front; any other key never shows its raw name.
          if (/^[a-z]/.test(text)) {
            if (!key) {
              general.push('Could not add to bag. Please try again.');
              return;
            }
            text = LABELS[key] + ' ' + text;
          }
          if (!/[.!?]$/.test(text)) text += '.';
          const el = key ? byKey[key] : null;
          const msg = key ? fieldError(key) : null;
          if (el && msg) {
            msg.textContent = text;
            msg.hidden = false;
            el.setAttribute('aria-invalid', 'true');
            if (!firstField) firstField = el;
          } else {
            general.push(text);
          }
        });
      } else if (typeof errors === 'string' && errors) {
        general.push(errors);
      }
      if (!general.length && !firstField) {
        // Shopify's own sentence when there is one; never a network or parse
        // error's technical text.
        const fromShopify =
          (typeof err.description === 'string' && err.description) ||
          (err.status && typeof err.message === 'string' ? err.message : '');
        general.push(fromShopify || 'Could not add to bag. Please try again.');
      }
      if (summary && general.length) {
        summary.textContent = Array.from(new Set(general)).join(' ');
        summary.hidden = false;
      }
      if (firstField && !firstField.disabled) firstField.focus();
      else if (summary && !summary.hidden) summary.focus();
    }

    function reset() {
      toggle.checked = false;
      inputs.forEach((el) => {
        el.value = '';
      });
      sync();
      updateCount();
    }

    toggle.disabled = false;
    if (control) control.disabled = true;
    toggle.addEventListener('change', sync);
    byKey.message?.addEventListener('input', updateCount);
    inputs.forEach((el) => {
      el.addEventListener('input', () => {
        if (el.getAttribute('aria-invalid') !== 'true') return;
        el.removeAttribute('aria-invalid');
        const msg = fieldError(el.getAttribute('data-gc-input'));
        if (msg) {
          msg.hidden = true;
          msg.textContent = '';
        }
      });
    });
    sync();
    updateCount();
    return { showErrors: showErrors, clearErrors: clearErrors, reset: reset };
  })();

  // Add to bag — AJAX cart. Always toasts on success, including when the same
  // variant is already in the bag (add.js still returns 200).
  if (pdpForm) {
    let pdpAdding = false;
    pdpForm.addEventListener('submit', (e) => {
      e.preventDefault();
      if (pdpAdding) return;
      if (pdpCta && pdpCta.disabled) return;
      const fd = new FormData(pdpForm);
      if (!fd.get('selling_plan')) fd.delete('selling_plan');
      if (giftRecipient) giftRecipient.clearErrors();
      pdpAdding = true;
      const label = pdpCta ? pdpCta.textContent : '';
      if (pdpCta) {
        pdpCta.disabled = true;
        pdpCta.textContent = 'Adding.';
      }
      addToCart(fd, true, giftRecipient ? giftRecipient.showErrors : null).then((cart) => {
        pdpAdding = false;
        if (pdpCta) {
          pdpCta.disabled = false;
          pdpCta.textContent = label;
        }
        if (cart && giftRecipient) giftRecipient.reset();
        updatePdpCta();
      });
    });
  }
  // Quick-add buttons outside the PDP form. With a variant id we do a real
  // add; without one we still confirm the click so feedback is never silent.
  document.querySelectorAll('[data-add-bag]').forEach((btn) => {
    if (btn.closest('#pdpForm')) return;
    btn.addEventListener('click', (e) => {
      const variantId = btn.getAttribute('data-variant-id') || btn.getAttribute('data-add-bag');
      if (variantId && /^\d+$/.test(variantId)) {
        e.preventDefault();
        if (btn.disabled) return;
        btn.disabled = true;
        addToCart({
          items: [{ id: Number(variantId), quantity: Math.max(1, parseInt(btn.getAttribute('data-quantity') || '1', 10) || 1) }],
        }).then(() => {
          btn.disabled = false;
        });
        return;
      }
      showToast('Added to bag');
    });
  });

  // PDP accordion — easy-ease open/close
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('.pdp-accordion details').forEach((details) => {
    const summary = details.querySelector('summary');
    const body = details.querySelector('.pdp-accordion-body');
    if (!summary || !body) return;

    let shell = details.querySelector('.pdp-accordion-shell');
    if (!shell) {
      shell = document.createElement('div');
      shell.className = 'pdp-accordion-shell';
      const inner = document.createElement('div');
      inner.className = 'pdp-accordion-shell-inner';
      body.parentNode.insertBefore(shell, body);
      shell.appendChild(inner);
      inner.appendChild(body);
    }

    let animating = false;
    let onEnd = () => {};
    let fallback = 0;
    function finish(cleanup) {
      if (!animating) return;
      animating = false;
      shell.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      cleanup();
    }

    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (animating) return;

      if (reduceMotion) {
        details.open = !details.open;
        return;
      }

      animating = true;
      const closing = details.open && !details.classList.contains('is-opening');

      if (closing) {
        details.classList.add('is-collapsing');
        shell.style.gridTemplateRows = '1fr';
        void shell.offsetHeight;
        shell.style.gridTemplateRows = '0fr';
        body.style.opacity = '0';
        body.style.transform = 'translateY(-6px)';

        onEnd = (ev) => {
          if (ev.target !== shell || ev.propertyName !== 'grid-template-rows') return;
          finish(() => {
            details.open = false;
            details.classList.remove('is-collapsing', 'is-opening');
            shell.style.gridTemplateRows = '';
            body.style.opacity = '';
            body.style.transform = '';
          });
        };
      } else {
        details.classList.add('is-opening');
        details.open = true;
        shell.style.gridTemplateRows = '0fr';
        body.style.opacity = '0';
        body.style.transform = 'translateY(-6px)';
        void shell.offsetHeight;
        shell.style.gridTemplateRows = '1fr';
        body.style.opacity = '1';
        body.style.transform = 'translateY(0)';

        onEnd = (ev) => {
          if (ev.target !== shell || ev.propertyName !== 'grid-template-rows') return;
          finish(() => {
            details.classList.remove('is-opening');
            shell.style.gridTemplateRows = '';
            body.style.opacity = '';
            body.style.transform = '';
          });
        };
      }

      shell.addEventListener('transitionend', onEnd);
      fallback = window.setTimeout(() => onEnd({ target: shell, propertyName: 'grid-template-rows' }), 500);
    });
  });

  // Shipping / returns side drawer — markup rendered by snippets/ship-drawer.liquid
  const shipTriggers = [...document.querySelectorAll('[data-open-shipping]')];
  const shipRoot = document.querySelector('.ship-drawer');
  if (shipTriggers.length && shipRoot) {
    let lastShipTrigger = shipTriggers[0];

    const openShip = () => {
      shipRoot.classList.add('is-open');
      shipRoot.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ship-drawer-open');
      shipRoot.querySelector('[data-close-shipping].ship-drawer-close')?.focus();
    };
    const closeShip = () => {
      shipRoot.classList.remove('is-open');
      shipRoot.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ship-drawer-open');
      lastShipTrigger.focus();
    };

    shipTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        lastShipTrigger = trigger;
        openShip();
      });
    });
    shipRoot.querySelectorAll('[data-close-shipping]').forEach((el) => {
      el.addEventListener('click', closeShip);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && shipRoot.classList.contains('is-open')) closeShip();
    });
  }

  // Search overlay — markup rendered by snippets/search-overlay.liquid;
  // results from Shopify predictive search.
  const overlay = document.getElementById('searchOverlay');
  const input = document.getElementById('searchInput');
  const results = document.getElementById('searchResults');
  const searchClose = document.getElementById('searchClose');
  if (overlay && input && results) {
    let searchTimer = null;
    let searchSeq = 0;

    const sizedImg = (url) => {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=100';
    };

    function renderSuggest(data, query) {
      const res = (data && data.resources && data.resources.results) || {};
      const products = res.products || [];
      const articles = res.articles || [];

      if (!products.length && !articles.length) {
        results.innerHTML = `<p class="search-empty">No results for “${escapeHtml(query)}”.</p>`;
        return;
      }

      let html = '';
      if (products.length) {
        html += `<div class="search-group"><h4>Products</h4>`;
        products.forEach((p) => {
          const img = sizedImg((p.featured_image && p.featured_image.url) || p.image);
          const price = typeof p.price === 'string' ? p.price : formatMoney(p.price);
          const sub = (p.type ? escapeHtml(p.type) + ' · ' : '') + escapeHtml(price);
          html += `<a class="search-item" href="${escapeHtml(p.url)}">
            <img src="${escapeHtml(img)}" alt="" />
            <div class="meta">
              <div class="name">${escapeHtml(p.title)}</div>
              <div class="sub">${sub}</div>
            </div>
          </a>`;
        });
        html += `</div>`;
      }
      if (articles.length) {
        html += `<div class="search-group"><h4>Journal</h4>`;
        articles.forEach((a) => {
          const img = sizedImg((a.featured_image && a.featured_image.url) || a.image);
          const cat = (a.tags && a.tags[0]) || 'Journal';
          html += `<a class="search-item" href="${escapeHtml(a.url)}">
            <img src="${escapeHtml(img)}" alt="" />
            <div class="meta">
              <div class="name">${escapeHtml(a.title)}</div>
              <div class="sub">${escapeHtml(cat)}</div>
            </div>
          </a>`;
        });
        html += `</div>`;
      }
      results.innerHTML = html;
    }

    function runSearch(query) {
      const q = (query || '').trim();
      if (!q) {
        searchSeq += 1;
        results.innerHTML = '';
        return;
      }
      const seq = ++searchSeq;
      const url =
        '/search/suggest.json?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product,article&resources[limit]=6&resources[options][unavailable_products]=show';
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (seq !== searchSeq) return;
          renderSuggest(data, q);
        })
        .catch(() => {});
    }

    function openSearch() {
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      results.innerHTML = '';
      setTimeout(() => input.focus(), 50);
    }
    function closeSearch() {
      overlay.classList.remove('open');
      document.body.style.overflow = '';
      input.value = '';
      results.innerHTML = '';
    }

    document.querySelectorAll('[data-open-search]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openSearch();
      });
    });
    searchClose?.addEventListener('click', closeSearch);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeSearch();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('open')) closeSearch();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      }
    });
    input.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const q = input.value;
      if (!q.trim()) {
        searchSeq += 1;
        results.innerHTML = '';
        return;
      }
      searchTimer = setTimeout(() => runSearch(q), 200);
    });
  }

  // Testimonials carousel — one quote at a time, horizontal slide
  const stage = document.querySelector('[data-testimonials]');
  if (stage) {
    const slides = Array.from(stage.querySelectorAll('[data-testimonial]'));
    const dotsWrap = document.querySelector('[data-testimonial-dots]');
    const prevBtn = document.querySelector('[data-testimonial-prev]');
    const nextBtn = document.querySelector('[data-testimonial-next]');
    let index = 0;
    let timer;
    let animating = false;

    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonial-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Show testimonial ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => {
        const forward = (i - index + slides.length) % slides.length;
        const backward = (index - i + slides.length) % slides.length;
        go(i, forward <= backward ? 1 : -1);
      });
      dotsWrap.appendChild(dot);
    });

    const dots = () => Array.from(dotsWrap.querySelectorAll('.testimonial-dot'));

    function go(i, dir = 1, instant = false) {
      const next = (i + slides.length) % slides.length;
      if (next === index && slides[index].classList.contains('is-active')) {
        restart();
        return;
      }
      if (animating && !instant) return;

      stage.style.setProperty('--slide-dir', String(dir));
      const outgoing = slides[index];
      const incoming = slides[next];

      if (instant) {
        slides.forEach((slide, n) => {
          slide.classList.remove('is-active', 'is-exiting');
          const on = n === next;
          slide.classList.toggle('is-active', on);
          if (on) slide.removeAttribute('hidden');
          else slide.setAttribute('hidden', '');
        });
        index = next;
        dots().forEach((dot, n) => {
          dot.setAttribute('aria-selected', n === index ? 'true' : 'false');
        });
        restart();
        return;
      }

      animating = true;
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exiting');

      incoming.removeAttribute('hidden');
      incoming.classList.remove('is-exiting');
      // Start off-stage, then slide in
      incoming.classList.remove('is-active');
      void incoming.offsetWidth;
      incoming.classList.add('is-active');

      index = next;
      dots().forEach((dot, n) => {
        dot.setAttribute('aria-selected', n === index ? 'true' : 'false');
      });

      window.setTimeout(() => {
        slides.forEach((slide, n) => {
          slide.classList.remove('is-exiting');
          if (n !== index) {
            slide.classList.remove('is-active');
            slide.setAttribute('hidden', '');
          }
        });
        animating = false;
      }, 560);

      restart();
    }

    function restart() {
      clearInterval(timer);
      timer = setInterval(() => go(index + 1, 1), 6000);
    }

    prevBtn?.addEventListener('click', () => go(index - 1, -1));
    nextBtn?.addEventListener('click', () => go(index + 1, 1));
    stage.addEventListener('mouseenter', () => clearInterval(timer));
    stage.addEventListener('mouseleave', restart);
    go(0, 1, true);
  }

  // Catalog filters / sort
  const catalogGrid = document.querySelector('[data-catalog-grid]');
  if (catalogGrid) {
    const products = Array.from(catalogGrid.querySelectorAll('.product'));
    const emptyEl = document.querySelector('[data-catalog-empty]');
    const minEl = document.querySelector('[data-catalog-price-min]');
    const maxEl = document.querySelector('[data-catalog-price-max]');
    const availInputs = Array.from(document.querySelectorAll('[data-catalog-avail]'));
    const catInputs = Array.from(document.querySelectorAll('[data-catalog-cat]'));
    const catActiveEl = document.querySelector('[data-catalog-cat-active]');
    const statusEl = document.querySelector('[data-catalog-status]');
    const catToggle = document.getElementById('filterToggle');
    const catDone = document.getElementById('filtersDone');
    // The heading names the active category (only the visible text: the
    // canonical, SEO title and markup stay as rendered).
    const titleEl = document.querySelector('.catalog-title');
    const baseTitle = titleEl ? titleEl.textContent.trim() : '';
    const baseDocTitle = document.title;
    const sortTrigger = document.querySelector('[data-catalog-sort-trigger]');
    const sortMenu = document.querySelector('[data-catalog-sort-menu]');
    const sortLabel = document.querySelector('[data-catalog-sort-label]');
    const sortOptions = Array.from(document.querySelectorAll('[data-catalog-sort-menu] [data-catalog-sort]'));
    const order = products.slice();
    let sort = 'relevance';

    function apply() {
      const min = parseFloat(minEl?.value || '0') || 0;
      const max = parseFloat(maxEl?.value || maxEl?.max || '135') || 135;
      const avail = availInputs.filter((i) => i.checked).map((i) => i.value);
      const cat = currentCategory();

      let list = order.filter((p) => {
        const price = parseFloat(p.dataset.price || '0');
        const stock = p.dataset.stock || 'in';
        const inPrice = price >= min && price <= max;
        const inAvail = avail.length === 0 ? true : avail.includes(stock);
        // Products with no category only show under All.
        const inCat = !cat || p.dataset.category === cat;
        return inPrice && inAvail && inCat;
      });

      if (sort === 'price-asc') list.sort((a, b) => parseFloat(a.dataset.price) - parseFloat(b.dataset.price));
      if (sort === 'price-desc') list.sort((a, b) => parseFloat(b.dataset.price) - parseFloat(a.dataset.price));
      if (sort === 'name-asc') list.sort((a, b) => (a.dataset.name || '').localeCompare(b.dataset.name || ''));
      if (sort === 'name-desc') list.sort((a, b) => (b.dataset.name || '').localeCompare(a.dataset.name || ''));

      products.forEach((p) => {
        p.hidden = true;
      });
      list.forEach((p) => {
        p.hidden = false;
        catalogGrid.appendChild(p);
      });

      if (emptyEl) emptyEl.hidden = list.length > 0;
      // The phone sheet's button says what it will show.
      if (catDone) catDone.textContent = 'Show ' + list.length + (list.length === 1 ? ' product' : ' products');
      return list.length;
    }

    // Category filter. One choice at a time, All by default. The choice lives
    // in ?category=<slug> (tights, socks, underwear, tops, gift-sets) so the
    // header dropdown can link straight to it and the view can be shared;
    // it is rewritten with replaceState, so no reload and no history entry.
    // An alias only lands when its category is listed (tops, once a top is in
    // the catalog); otherwise the view falls back to All, as for any unknown slug.
    const CATEGORY_ALIASES = { sock: 'socks', pantyhose: 'tights', tight: 'tights', hosiery: 'tights', top: 'tops', tee: 'tops', tees: 'tops', 't-shirt': 'tops', 't-shirts': 'tops', tshirt: 'tops', tshirts: 'tops', shirt: 'tops', shirts: 'tops', 'gift-set': 'gift-sets', gifts: 'gift-sets', sets: 'gift-sets' };
    function currentCategory() {
      const on = catInputs.find((i) => i.checked);
      return on ? on.value : '';
    }
    function categoryInput(slug) {
      return catInputs.find((i) => i.value === slug) || catInputs.find((i) => i.value === '');
    }
    function showCategoryState() {
      const on = catInputs.find((i) => i.checked && i.value);
      const label = on ? on.dataset.label || '' : '';
      const active = on ? on.value : '';
      if (catActiveEl) catActiveEl.textContent = label;
      if (catToggle) {
        if (label) catToggle.setAttribute('aria-label', 'Filter, ' + label);
        else catToggle.removeAttribute('aria-label');
      }
      if (titleEl) titleEl.textContent = label || baseTitle;
      if (baseTitle && baseDocTitle.indexOf(baseTitle) === 0) {
        document.title = label ? label + baseDocTitle.slice(baseTitle.length) : baseDocTitle;
      }
      // The header dropdown and the mobile menu mark where the shopper is.
      document.querySelectorAll('a[data-category-link]').forEach((a) => {
        let current = false;
        try {
          const url = new URL(a.href, window.location.href);
          current = url.pathname === window.location.pathname && (url.searchParams.get('category') || '') === active;
        } catch (err) {
          current = false;
        }
        if (current) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }
    function writeCategoryUrl(slug) {
      try {
        const url = new URL(window.location.href);
        if (slug) url.searchParams.set('category', slug);
        else url.searchParams.delete('category');
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
      } catch (err) {
        /* old browser: the filter still works, the URL just does not follow */
      }
    }
    function announce(count) {
      if (statusEl) statusEl.textContent = 'Showing ' + count + (count === 1 ? ' product' : ' products');
    }
    function chooseCategory(slug) {
      const input = categoryInput(slug);
      if (input) input.checked = true;
      writeCategoryUrl(currentCategory());
      showCategoryState();
      announce(apply());
    }

    if (catInputs.length) {
      let raw = '';
      let hasParam = false;
      try {
        const params = new URLSearchParams(window.location.search);
        hasParam = params.has('category');
        raw = (params.get('category') || '').trim().toLowerCase();
      } catch (err) {
        raw = '';
      }
      const wanted = CATEGORY_ALIASES[raw] || raw;
      const match = wanted ? catInputs.find((i) => i.value === wanted) : null;
      const start = match || categoryInput('');
      if (start) start.checked = true;
      // Tidy the address: alias to slug, unknown or empty category dropped.
      if (hasParam && (!match || raw !== match.value)) writeCategoryUrl(match ? match.value : '');
      showCategoryState();

      catInputs.forEach((i) => i.addEventListener('change', () => chooseCategory(currentCategory())));

      // Header dropdown and mobile menu links that point at this same page
      // filter in place instead of reloading.
      document.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('a[data-category-link]');
        if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        let slug = '';
        try {
          const url = new URL(a.href, window.location.href);
          if (url.pathname !== window.location.pathname) return;
          slug = url.searchParams.get('category') || '';
        } catch (err) {
          return;
        }
        e.preventDefault();
        chooseCategory(slug);
        const top = catalogGrid.closest('.catalog')?.getBoundingClientRect().top;
        if (typeof top === 'number' && top < 0) window.scrollTo({ top: 0 });
        // The link that was used is about to hide (the dropdown closes, or the
        // mobile menu closes without restoring focus because it expects a
        // page load), so place focus on the heading that now names the view.
        if (titleEl) titleEl.focus({ preventScroll: true });
      });
    }

    function setSort(value, label) {
      sort = value;
      if (sortLabel) sortLabel.textContent = label;
      sortOptions.forEach((btn) => {
        btn.setAttribute('aria-selected', btn.dataset.catalogSort === value ? 'true' : 'false');
      });
      sortMenu.hidden = true;
      sortTrigger?.setAttribute('aria-expanded', 'false');
      apply();
    }

    sortTrigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = sortMenu.hidden;
      sortMenu.hidden = !open;
      sortTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    sortOptions.forEach((btn) => {
      btn.addEventListener('click', () => setSort(btn.dataset.catalogSort, btn.textContent.trim()));
    });
    document.addEventListener('click', () => {
      if (!sortMenu || sortMenu.hidden) return;
      sortMenu.hidden = true;
      sortTrigger?.setAttribute('aria-expanded', 'false');
    });

    availInputs.forEach((i) => i.addEventListener('change', () => announce(apply())));
    minEl?.addEventListener('input', apply);
    maxEl?.addEventListener('input', apply);
    apply();
    // layout/theme.liquid flags ?category= on <html> so CSS can hide the
    // other cards before first paint; [hidden] is in charge from here on.
    delete document.documentElement.dataset.cat;
  }

  // Collection filter sheet — mobile only. The filters aside is always in the
  // flow on desktop, so the toggle is a no-op above 820px.
  const filterToggle = document.getElementById('filterToggle');
  const filtersPanel = document.getElementById('catalogFilters');
  if (filterToggle && filtersPanel) {
    const filtersDone = document.getElementById('filtersDone') || filtersPanel.querySelector('.filters__done');
    const mq = window.matchMedia('(max-width: 820px)');
    const filtersOpen = () => filtersPanel.classList.contains('is-open');

    function openFilters() {
      if (!mq.matches) return;
      filtersPanel.classList.add('is-open');
      document.body.classList.add('no-scroll');
      filterToggle.setAttribute('aria-expanded', 'true');
    }
    function closeFilters(restoreFocus) {
      if (!filtersOpen()) return;
      filtersPanel.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      filterToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus !== false) filterToggle.focus();
    }

    if (!filterToggle.hasAttribute('aria-expanded')) filterToggle.setAttribute('aria-expanded', 'false');
    filterToggle.setAttribute('aria-controls', 'catalogFilters');

    // No stopPropagation: letting the click reach document also closes the
    // sort menu, so the two never sit open at once.
    filterToggle.addEventListener('click', () => {
      if (filtersOpen()) closeFilters();
      else openFilters();
    });
    filtersDone?.addEventListener('click', () => closeFilters());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && filtersOpen()) {
        e.preventDefault();
        closeFilters();
      }
    });

    // Resizing up to desktop must not leave body scroll locked.
    const onFilterMq = () => {
      if (!mq.matches) closeFilters(false);
    };
    if (mq.addEventListener) mq.addEventListener('change', onFilterMq);
    else if (mq.addListener) mq.addListener(onFilterMq);
  }

  // Cart page — quantity and remove via the Cart AJAX API. Everything updates
  // in place: line total, subtotal, header counts, free-shipping bar. The page
  // only reloads when the bag empties, so the empty state can render.
  (function cartPage() {
    // One entry per line, outermost element wins if the hook is nested.
    const rows = [];
    const seenLines = new Set();
    document.querySelectorAll('[data-cart-line]').forEach((el) => {
      const n = parseInt(el.getAttribute('data-cart-line'), 10);
      if (!n || seenLines.has(n)) return;
      seenLines.add(n);
      rows.push(el);
    });
    if (!rows.length) return;

    const subtotalEls = document.querySelectorAll('[data-cart-subtotal]');
    const countNote = document.querySelector('.cart-count-note');
    // "Shipping and taxes calculated at checkout." hides once only gift cards are left.
    const shipNote = document.querySelector('.cart-note');
    let busy = false;

    const lines = rows.map((row, i) => {
      // The hook may sit on the row or on the qty control inside it.
      const scope = row.closest('tr, .cart-line, [data-cart-item]') || row;
      return {
        index: i + 1,
        row: row,
        scope: scope,
        minus: scope.querySelector('[data-cart-line-minus]') || row.querySelector('[data-qty-dec]'),
        plus: scope.querySelector('[data-cart-line-plus]') || row.querySelector('[data-qty-plus], [data-qty-inc]'),
        qty: scope.querySelector('[data-cart-qty]') || row.querySelector('input[type="number"]'),
        total: scope.querySelector('[data-line-total]') || scope.querySelector('[data-line-price]'),
        remove: scope.querySelector('[data-cart-remove]'),
      };
    });

    function reindex() {
      lines.forEach((l, i) => {
        l.index = i + 1;
        l.row.setAttribute('data-cart-line', String(l.index));
        if (l.qty) l.qty.setAttribute('data-line', String(l.index));
      });
    }

    function setBusy(line, on) {
      busy = on;
      line.scope.classList.toggle('is-updating', on);
      [line.minus, line.plus, line.qty, line.remove].forEach((el) => {
        if (!el) return;
        if ('disabled' in el) el.disabled = on;
        el.setAttribute('aria-busy', on ? 'true' : 'false');
        if (el === line.remove && !('disabled' in el)) {
          el.classList.toggle('is-disabled', on);
        }
      });
    }

    function currentQty(line) {
      const raw = (line.qty && line.qty.value) || line.scope.getAttribute('data-quantity') || '1';
      return Math.max(0, parseInt(raw, 10) || 0);
    }

    function renderCart(cart, line, quantity) {
      subtotalEls.forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      setCartCounts(cart.item_count);
      updateFreeShipBar(cart);
      if (shipNote) shipNote.hidden = freeShipTotals(cart).giftOnly;
      if (countNote) {
        countNote.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
      }

      if (quantity === 0) {
        line.scope.remove();
        const at = lines.indexOf(line);
        if (at > -1) lines.splice(at, 1);
        reindex();
        showToast('Removed from bag');
        return;
      }

      const item = cart.items[line.index - 1];
      if (item) {
        if (line.qty) line.qty.value = String(item.quantity);
        line.scope.setAttribute('data-quantity', String(item.quantity));
        if (line.total) line.total.textContent = formatMoney(item.final_line_price);
        // Shopify clamps to available stock; say so rather than silently differ.
        if (item.quantity < quantity) {
          showToast('Only ' + item.quantity + ' available');
        }
      }
    }

    function change(line, quantity) {
      if (busy) return;
      const qty = Math.max(0, parseInt(quantity, 10) || 0);
      setBusy(line, true);
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line.index, quantity: qty }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(err));
          return res.json();
        })
        .then((cart) => {
          if (cart.item_count === 0) {
            window.location.reload();
            return;
          }
          setBusy(line, false);
          renderCart(cart, line, qty);
        })
        .catch((err) => {
          setBusy(line, false);
          if (line.qty) line.qty.value = String(line.scope.getAttribute('data-quantity') || line.qty.value);
          showToast(cartErrorText(err, 'Could not update bag'));
        });
    }

    lines.forEach((line) => {
      if (line.qty && !line.scope.hasAttribute('data-quantity')) {
        line.scope.setAttribute('data-quantity', String(currentQty(line)));
      }
      line.minus?.addEventListener('click', (e) => {
        e.preventDefault();
        change(line, Math.max(0, currentQty(line) - 1));
      });
      line.plus?.addEventListener('click', (e) => {
        e.preventDefault();
        change(line, currentQty(line) + 1);
      });
      line.qty?.addEventListener('change', () => change(line, currentQty(line)));
      line.qty?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault(); // never let the qty field submit the cart form
        change(line, currentQty(line));
      });
      line.remove?.addEventListener('click', (e) => {
        e.preventDefault();
        change(line, 0);
      });
    });
  })();
  // Homepage store reviews (2026-10-27, sections/store-reviews.liquid): rail
  // arrows + progress, Read more on clamped cards, and a live refresh from
  // Judge.me's public widget data. product_id 0 = store reviews. The response
  // carries PUBLISHED reviews only, so a review hidden in Judge.me drops out
  // here too. The server-rendered list (newest 25 from the reviews_grid
  // metafield) stays whenever Judge.me cannot be reached.
  (function storeReviews() {
    const sec = document.querySelector('[data-store-reviews]');
    if (!sec) return;
    const rail = sec.querySelector('[data-sr-rail]');
    const thumb = sec.querySelector('[data-sr-thumb]');
    const prev = sec.querySelector('[data-sr-prev]');
    const next = sec.querySelector('[data-sr-next]');
    if (!rail || !thumb || !prev || !next) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const step = () => {
      const card = rail.querySelector('.store-review');
      if (!card) return rail.clientWidth;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 12;
      const per = Math.max(1, Math.floor((rail.clientWidth + gap) / (card.offsetWidth + gap)));
      return per * (card.offsetWidth + gap);
    };
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      const frac = rail.scrollWidth > 0 ? Math.min(1, rail.clientWidth / rail.scrollWidth) : 1;
      const pos = max > 0 ? rail.scrollLeft / max : 0;
      thumb.style.width = frac * 100 + '%';
      thumb.style.transform = 'translateX(' + pos * (1 / frac - 1) * 100 + '%)';
      // aria-disabled, not disabled: a focused arrow that disabled itself at the
      // end would drop keyboard focus to <body>. At the end it just does nothing.
      prev.setAttribute('aria-disabled', rail.scrollLeft <= 2 ? 'true' : 'false');
      next.setAttribute('aria-disabled', rail.scrollLeft >= max - 2 ? 'true' : 'false');
    };
    const go = (btn, dir) => {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      rail.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => go(prev, -1));
    next.addEventListener('click', () => go(next, 1));
    rail.addEventListener('scroll', sync, { passive: true });

    // Read more only on cards whose text is cut off at this width. Measured at
    // the 4-line clamp; a card that needs the button drops to 3 lines
    // (.has-more) so the button fits without slicing a line of text.
    const checkMore = () => {
      rail.querySelectorAll('.store-review').forEach((card) => {
        const p = card.querySelector('.store-review-body p');
        const more = card.querySelector('.store-review-more');
        if (!p || !more || card.classList.contains('is-open')) return;
        card.classList.remove('has-more');
        const cut = p.scrollHeight > p.clientHeight + 2;
        card.classList.toggle('has-more', cut);
        more.hidden = !cut;
      });
    };
    rail.addEventListener('click', (e) => {
      const more = e.target.closest('.store-review-more');
      if (!more) return;
      const card = more.closest('.store-review');
      const open = card.classList.toggle('is-open');
      more.textContent = open ? 'Show less' : 'Read more';
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { checkMore(); sync(); }, 150);
    });
    const settle = () => { checkMore(); sync(); };
    settle();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(settle);

    // Live refresh
    const jmShop = sec.dataset.jmShop;
    const textOf = (html) => {
      const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    };
    const monthOf = (iso) => {
      const d = new Date(iso);
      return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    const fetchPage = async (n) => {
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 6000) : null;
      try {
        const q = new URLSearchParams({
          url: jmShop, shop_domain: jmShop, platform: 'shopify',
          page: String(n), per_page: '30', product_id: '0',
        });
        const res = await fetch('https://judge.me/reviews/reviews_for_widget?' + q.toString(),
          { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined });
        if (!res.ok) return null;
        const data = await res.json();
        return data && Array.isArray(data.reviews) ? data : null;
      } catch (e) {
        return null;
      } finally {
        if (timer) clearTimeout(timer);
      }
    };
    const cardFor = (w) => {
      const li = document.createElement('li');
      li.className = 'store-review';
      const top = document.createElement('div');
      top.className = 'store-review-top';
      const stars = document.createElement('span');
      stars.className = 'store-review-stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', 'Rated ' + w.rating + ' out of 5 stars');
      stars.style.setProperty('--fill', w.rating * 20 + '%');
      top.appendChild(stars);
      const month = monthOf(w.created_at);
      if (month) {
        const tm = document.createElement('time');
        tm.dateTime = w.created_at;
        tm.textContent = month;
        top.appendChild(tm);
      }
      li.appendChild(top);
      const quote = document.createElement('blockquote');
      quote.className = 'store-review-body';
      const title = String(w.title || '').trim();
      if (title) {
        const t = document.createElement('strong');
        t.textContent = title;
        quote.appendChild(t);
      }
      const body = textOf(w.body_html);
      if (body) {
        const pEl = document.createElement('p');
        pEl.textContent = body;
        quote.appendChild(pEl);
      }
      li.appendChild(quote);
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'store-review-more';
      more.hidden = true;
      more.setAttribute('aria-expanded', 'false');
      more.textContent = 'Read more';
      li.appendChild(more);
      const foot = document.createElement('footer');
      const cite = document.createElement('cite');
      cite.textContent = w.is_anonymous_reviewer === false ? (String(w.reviewer_name || '').trim() || 'Anonymous') : 'Anonymous';
      foot.appendChild(cite);
      if (w.verified_buyer === true) {
        const v = document.createElement('span');
        v.className = 'store-review-verified';
        v.textContent = 'Verified buyer';
        foot.appendChild(v);
      }
      li.appendChild(foot);
      return li;
    };
    const refresh = async () => {
      if (!jmShop || typeof fetch !== 'function' || typeof DOMParser !== 'function') return;
      const first = await fetchPage(1);
      if (!first) return;
      let list = first.reviews;
      const pages = Math.min(5, parseInt(first.pagination && first.pagination.total_pages, 10) || 1); // up to 150 newest
      for (let n = 2; n <= pages; n++) {
        const more = await fetchPage(n);
        if (!more) return; // incomplete: keep the server-rendered list
        list = list.concat(more.reviews);
      }
      list = list.filter((w) => w && Number.isInteger(w.rating) && w.rating >= 1 && w.rating <= 5);
      const total = parseInt(first.number_of_reviews, 10);
      const avg = parseFloat(first.average_rating);
      if (!list.length || !(total > 0) || !(avg >= 1 && avg <= 5)) return;
      const frag = document.createDocumentFragment();
      list.forEach((w) => frag.appendChild(cardFor(w)));
      rail.replaceChildren(frag);
      const score = avg.toFixed(1);
      const scoreEl = sec.querySelector('[data-sr-score]');
      const starsEl = sec.querySelector('[data-sr-stars]');
      const countEl = sec.querySelector('[data-sr-count]');
      if (scoreEl) scoreEl.textContent = score;
      if (starsEl) {
        starsEl.style.setProperty('--fill', (avg / 5) * 100 + '%');
        starsEl.setAttribute('aria-label', 'Rated ' + score + ' out of 5');
      }
      if (countEl) countEl.textContent = String(total);
      settle();
    };
    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          near.disconnect();
          refresh();
        }
      }, { rootMargin: '600px 0px' });
      near.observe(sec);
    } else {
      refresh();
    }
  })();
})();
